import { useState } from 'react';
import { Dialog, VStack, Button, HStack, Text, Box } from '@chakra-ui/react';
import { MdCameraAlt, MdPhoto, MdClose as CloseIcon } from 'react-icons/md';
import { useImagePicker } from '@/hooks/useImagePicker';
import { useRecipeImageUpload } from '@/hooks/useRecipeImageUpload';
import { toaster } from '@/lib/toaster';

interface ChangePhotoDialogProps {
  isOpen?: boolean;
  onClose?: () => void;
  recipeId?: string; // Always a UUID string
}

export default function ChangePhotoDialog({
  isOpen = false,
  onClose,
  recipeId,
}: ChangePhotoDialogProps) {
  const [isPickingImage, setIsPickingImage] = useState(false);

  const { pickImage } = useImagePicker();
  const { uploadImage, isUploading } = useRecipeImageUpload({
    recipeId: recipeId!,
    onSuccess: onClose,
  });

  const handleImageSource = async (source: 'camera' | 'gallery') => {
    try {
      setIsPickingImage(true);
      const file = await pickImage(source);
      uploadImage(file);
    } catch (error) {
      console.error(`${source} error:`, error);

      // Only show error toast if user didn't cancel
      const errorMessage = (error as Error).message;
      if (errorMessage !== 'User cancelled') {
        toaster.create({
          title: source === 'camera' ? 'Camera error' : 'Gallery error',
          description: source === 'camera'
            ? 'Failed to access camera'
            : 'Failed to access photo gallery',
          type: 'error',
        });
      }
    } finally {
      setIsPickingImage(false);
    }
  };

  const isBusy = isPickingImage || isUploading;

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={onClose ? (details) => details.open || onClose() : undefined}
    >
      <Dialog.Backdrop style={{ background: 'rgba(0,0,0,0.5)' }} />
      <Dialog.Positioner>
        <Dialog.Content
          mx={4}
          bg="bg.surface"
          border="1px solid"
          borderColor="border.muted"
          borderRadius="2xl"
          shadow="xl"
          overflow="hidden"
        >
          {/* Header */}
          <Box px={5} pt={5} pb={4} borderBottomWidth="1px" borderColor="border.muted">
            <HStack justify="space-between" align="center">
              <Text fontFamily="heading" fontWeight="800" fontSize="md" color="text.primary">
                Change Photo
              </Text>
              <Box
                as="button"
                aria-label="Close"
                onClick={onClose}
                w="30px"
                h="30px"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="text.muted"
                bg="transparent"
                border="none"
                cursor="pointer"
                _hover={{ bg: 'bg.muted', color: 'text.primary' }}
                transition="all 0.15s"
              >
                <CloseIcon size={18} />
              </Box>
            </HStack>
          </Box>

          {/* Actions */}
          <Dialog.Body px={5} pt={4} pb={5}>
            <VStack gap={3} w="100%">
              <Button
                w="100%"
                h="46px"
                onClick={() => handleImageSource('camera')}
                loading={isBusy}
                disabled={isBusy}
                bg="accent.emphasis"
                color="white"
                _hover={{ bg: 'accent.emphasisHover' }}
                fontFamily="heading"
                fontWeight="800"
                fontSize="sm"
                letterSpacing="0.06em"
                textTransform="uppercase"
                borderRadius="lg"
              >
                <MdCameraAlt size={18} />
                Take Photo
              </Button>

              <Button
                w="100%"
                h="46px"
                onClick={() => handleImageSource('gallery')}
                loading={isBusy}
                disabled={isBusy}
                variant="outline"
                bg="transparent"
                border="1px solid"
                borderColor="border.muted"
                color="text.primary"
                _hover={{ bg: 'bg.muted' }}
                fontFamily="body"
                fontWeight="500"
                fontSize="sm"
                borderRadius="lg"
              >
                <MdPhoto size={18} />
                Choose from Gallery
              </Button>

              {isUploading && (
                <Text fontSize="xs" color="text.muted" fontWeight="300">
                  Uploading image...
                </Text>
              )}
            </VStack>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
